import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Save, Target, ShieldCheck, Info, Link as LinkIcon, Users, Check, Search, Filter, ChevronUp, ChevronDown } from "lucide-react";
import Swal from "sweetalert2";

import {
    createCampagneRequest,
    updateCampagneRequest,
    fetchCampagnesRequest
} from "../../redux/actions/campagnesActions";
import { getAllEntitesRequest } from "../../redux/actions/entitesActions";
import { getAllJobsRequest } from "../../redux/actions/Jobactions";
import { getAllUsersRequest } from "../../redux/actions/userActions";
import MultiSelectDropdown from "../../Ui/UiJs/MultiSelectDropdown";

/* ─── Helpers ─── */
const toDateInputValue = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "" : dt.toISOString().split("T")[0];
};

const uniqNums = (arr) =>
    Array.from(new Set((arr || []).map((x) => Number(x)).filter((x) => !Number.isNaN(x))));

/* ─── PillRadio helper ─── */
function PillRadioGroup({ name, options, value, onChange }) {
    return (
        <div className="cr-radio-group">
            {options.map(opt => (
                <label key={opt.value} className="cr-pill-radio">
                    <input
                        type="radio"
                        name={name}
                        value={opt.value}
                        checked={String(value) === String(opt.value)}
                        onChange={() => onChange(opt.value)}
                    />
                    <span className={`cr-pill${String(value) === String(opt.value) ? " active" : ""}`}>
                        {opt.label}
                    </span>
                </label>
            ))}
        </div>
    );
}

/* ─── Inline Users Panel (Exact replication from Training) ─── */
function InlineUsersPanel({ users, usersState, setUsersState, setSelectedUsers, entiteIds, posteIds }) {
    const [search, setSearch] = useState("");
    const [autoFilter, setAutoFilter] = useState(false);

    const AVATAR_COLORS = ["#2f4595", "#dc3545", "#0891b2", "#059669", "#707ea7", "#707ea7"];
    const getAvatarColor = (id) => AVATAR_COLORS[+id % AVATAR_COLORS.length];

    const getInitials = (u) => {
        const first = (u.firstName || u.prenom || u.firstName || "?")[0];
        const last = (u.lastName || u.nom || "")[0] || "";
        return `${first}${last}`.toUpperCase();
    };
    const getFullName = (u) =>
        `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() || `User #${u.id}`;

    const baseList = useMemo(() => {
        if (!autoFilter || (!entiteIds.length && !posteIds.length)) return usersState || [];
        return (usersState || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [usersState, autoFilter, entiteIds, posteIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseList;
        return baseList.filter(u =>
            `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""} ${u.email || ""}`.toLowerCase().includes(q)
        );
    }, [baseList, search]);

    const toggleUser = (userId) => {
        const updated = usersState.map(u =>
            +u.id === +userId ? { ...u, checked: !u.checked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const toggleAll = () => {
        const allChecked = filtered.every(u => u.checked);
        const filteredIds = new Set(filtered.map(u => +u.id));
        const updated = usersState.map(u =>
            filteredIds.has(+u.id) ? { ...u, checked: !allChecked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter(u => u.checked));
    };

    const clearAll = () => {
        const updated = usersState.map(u => ({ ...u, checked: false }));
        setUsersState(updated);
        setSelectedUsers([]);
    };

    const checkedUsers = (usersState || []).filter(u => u.checked);
    const allFilteredChecked = filtered.length > 0 && filtered.every(u => u.checked);
    const hasAutoFilterOption = entiteIds.length > 0 || posteIds.length > 0;

    return (
        <div className="cr-users-panel">
            <div className="cr-users-panel__header">
                <div className="cr-users-panel__title">
                    <Users size={14} />
                    Collaborateurs
                </div>
                <div className="cr-users-header-right">
                    {hasAutoFilterOption && (
                        <button
                            type="button"
                            className={`cr-auto-filter-btn${autoFilter ? " active" : ""}`}
                            onClick={() => setAutoFilter(p => !p)}
                            title="Filtrer selon les entités et postes sélectionnés"
                        >
                            <Filter size={12} />
                            Filtrer par entités/postes
                        </button>
                    )}
                    <div className="cr-users-search">
                        <Search size={12} className="cr-users-search-icon" />
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="cr-users-search-input"
                        />
                    </div>
                </div>
            </div>

            {checkedUsers.length > 0 && (
                <div className="cr-users-summary">
                    <div className="cr-avatar-stack">
                        {checkedUsers.slice(0, 5).map(u => (
                            <div
                                key={u.id}
                                className="cr-avatar-stack__item"
                                style={{ background: getAvatarColor(u.id) }}
                                title={getFullName(u)}
                            >
                                {getInitials(u)}
                            </div>
                        ))}
                        {checkedUsers.length > 5 && (
                            <div className="cr-avatar-stack__more">+{checkedUsers.length - 5}</div>
                        )}
                    </div>
                    <span className="cr-users-summary__label">
                        <strong>{checkedUsers.length}</strong> collaborateur{checkedUsers.length > 1 ? "s" : ""} sélectionné{checkedUsers.length > 1 ? "s" : ""}
                    </span>
                    <button type="button" className="cr-users-summary__clear" onClick={clearAll}>
                        <X size={11} /> Tout désélectionner
                    </button>
                </div>
            )}

            {(usersState || []).length === 0 ? (
                <div className="cr-users-empty">
                    <Users size={28} style={{ color: "#d1d5db", marginBottom: 8 }} />
                    <div>Aucun utilisateur disponible</div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="cr-users-empty">
                    Aucun résultat{search ? ` pour « ${search} »` : " avec ce filtre"}
                </div>
            ) : (
                <>
                    <div className="cr-users-select-all" onClick={toggleAll}>
                        <div className={`cr-user-checkbox${allFilteredChecked ? " checked" : ""}`}>
                            {allFilteredChecked && <Check size={10} />}
                        </div>
                        <span className="cr-users-select-text">
                            {allFilteredChecked ? "Tout désélectionner" : "Tout sélectionner"} ({filtered.length})
                        </span>
                    </div>
                    <div className="cr-users-list">
                        {filtered.map(u => (
                            <div
                                key={u.id}
                                className={`cr-user-row${u.checked ? " checked" : ""}`}
                                onClick={() => toggleUser(u.id)}
                            >
                                <div className={`cr-user-checkbox${u.checked ? " checked" : ""}`}>
                                    {u.checked && <Check size={10} />}
                                </div>
                                <div
                                    className="cr-user-avatar"
                                    style={u.checked ? { background: getAvatarColor(u.id), color: "#fff" } : {}}
                                >
                                    {getInitials(u)}
                                </div>
                                <div className="cr-user-info">
                                    <div className="cr-user-name">{getFullName(u)}</div>
                                    {u.email && <div className="cr-user-email">{u.email}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const EMPTY_FORM = {
    nom: "",
    reference: "",
    description: "",
    anneeCampagne: new Date().getFullYear(),
    dateDebut: "",
    dateFin: "",
    validation: "MANAGER",
    nombreMaxObjectifs: 5,
    nombreObjectifsQuantitatifs: 0,
    nombreObjectifsQualitatifs: 0,
    revisionObjectifs: false,
    campagneFormationAssocieeId: "",
    campagneCompetenceAssocieeId: "",
    campagneNMoins1Id: "",
    planificationRelance: false,
    dateRelance: "",
    dateRelance2: "",
    dateRelance3: "",
    status: true,
    isDefault: false,
    activationPurpose: "DEMANDER",
    activationUserIds: [],
    activationDateDebut: "",
    activationDateFin: "",
    envoiEmails: "INSTANTANE",
    dateEnvoiEmails: "",
};

const CampagnePerformanceForm = ({ open, campagneId, campagnes, onClose }) => {
    const dispatch = useDispatch();
    const campagnesState = useSelector(s => s.campagnes || {});
    const allCampagnes = campagnesState.list || [];

    const { entites = [] } = useSelector(state => state.entite || {});
    const { jobs = [] } = useSelector(state => state.jobs || {});
    const { users = [] } = useSelector(state => state.user || {});

    const campagne = useMemo(() => 
        campagneId ? (campagnes || []).find(c => +c.id === +campagneId) : null,
    [campagneId, campagnes]);

    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState("id");

    /* Population State (Identifying with Training) */
    const [filterMode, setFilterMode] = useState("IN");
    const [managerLevelFilter, setManagerLevelFilter] = useState("");
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [initialUserIds, setInitialUserIds] = useState([]);
    const [showUsersPanel, setShowUsersPanel] = useState(false);

    /* Activation State */
    const [activationUserIds, setActivationUserIds] = useState([]);

    const filteredUsersForActivation = useMemo(() => {
        if (!entiteIds.length && !posteIds.length) return users || [];
        return (users || []).filter(u => {
            const matchEnt = entiteIds.length === 0 || entiteIds.includes(+u.entiteId) || entiteIds.includes(+u.entityId);
            const matchPost = posteIds.length === 0 || posteIds.includes(+u.posteId) || posteIds.includes(+u.jobId);
            return matchEnt && matchPost;
        });
    }, [users, entiteIds, posteIds]);

    useEffect(() => {
        dispatch(getAllEntitesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllUsersRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (campagne) {
            setForm({
                nom: campagne.nom || "",
                reference: campagne.reference || "",
                description: campagne.description || "",
                anneeCampagne: campagne.anneeCampagne || new Date().getFullYear(),
                dateDebut: toDateInputValue(campagne.dateDebut),
                dateFin: toDateInputValue(campagne.dateFin),
                validation: campagne.validation || "MANAGER",
                nombreMaxObjectifs: campagne.nombreMaxObjectifs || 5,
                nombreObjectifsQuantitatifs: campagne.nombreObjectifsQuantitatifs || 0,
                nombreObjectifsQualitatifs: campagne.nombreObjectifsQualitatifs || 0,
                revisionObjectifs: Boolean(campagne.revisionObjectifs),
                campagneFormationAssocieeId: campagne.campagneFormationAssocieeId || "",
                campagneCompetenceAssocieeId: campagne.campagneCompetenceAssocieeId || "",
                campagneNMoins1Id: campagne.campagneNMoins1Id || "",
                planificationRelance: Boolean(campagne.planificationRelance),
                dateRelance: toDateInputValue(campagne.dateRelance),
                dateRelance2: toDateInputValue(campagne.dateRelance2),
                dateRelance3: toDateInputValue(campagne.dateRelance3),
                status: Boolean(campagne.status),
                isDefault: Boolean(campagne.isDefault),
                activationPurpose: campagne.activationPurpose || "DEMANDER",
                activationDateDebut: toDateInputValue(campagne.activationDateDebut),
                activationDateFin: toDateInputValue(campagne.activationDateFin),
                envoiEmails: campagne.envoiEmails || "INSTANTANE",
                dateEnvoiEmails: toDateInputValue(campagne.dateEnvoiEmails),
            });
            setFilterMode(campagne.filterMode || "IN");
            setManagerLevelFilter(campagne.managerLevelFilter || "");
            setEntiteIds(uniqNums(campagne.entiteIds || []));
            setPosteIds(uniqNums(campagne.posteIds || []));
            setInitialUserIds(uniqNums(campagne.userIds || []));
            setActivationUserIds(uniqNums(campagne.activationUserIds || []));
        } else {
            setForm(EMPTY_FORM);
            setFilterMode("IN"); setManagerLevelFilter(""); setEntiteIds([]); setPosteIds([]);
            setInitialUserIds([]); setShowUsersPanel(false);
            setActivationUserIds([]);
        }
    }, [campagne, open]);

    useEffect(() => {
        const prevMap = new Map((usersState || []).map(u => [+u.id, !!u.checked]));
        const initSet = new Set((initialUserIds || []).map(x => +x));
        const next = (users || []).map(u => ({
            ...u,
            checked: prevMap.has(+u.id) ? prevMap.get(+u.id) : initSet.has(+u.id)
        }));
        setUsersState(next);
        setSelectedUsers(next.filter(u => u.checked));
    }, [users, initialUserIds]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const qant = parseInt(form.nombreObjectifsQuantitatifs || 0);
    const qual = parseInt(form.nombreObjectifsQualitatifs || 0);
    const max = parseInt(form.nombreMaxObjectifs || 0);
    const reste = max - (qant + qual);
    const isExceeded = reste < 0;

    const validate = () => {
        const e = {};
        if (!form.nom.trim()) e.nom = "Le nom est obligatoire";
        if (!form.reference.trim()) e.reference = "La référence est obligatoire";
        if (!max || max < 1) e.nombreMaxObjectifs = "Min 1";
        
        if (isExceeded) {
            e.objectifs = "La somme des objectifs quantitatifs et qualitatifs ne doit pas dépasser le total";
        }

        if (form.planificationRelance && !form.dateRelance) {
            e.dateRelance = "La date de la relance 1 est obligatoire si activée";
        }

        if (form.envoiEmails === "DIFFERE" && !form.dateEnvoiEmails) {
            e.dateEnvoiEmails = "La date d'envoi est obligatoire pour l'envoi différé";
        }
        
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        const payload = {
            ...form,
            typeCampagne: "PERFORMANCE",
            filterMode,
            managerLevelFilter: managerLevelFilter || null,
            entiteIds: uniqNums(entiteIds),
            posteIds: uniqNums(posteIds),
            userIds: uniqNums(selectedUsers.map(u => u.id)),
            anneeCampagne: parseInt(form.anneeCampagne),
            nombreMaxObjectifs: max,
            nombreObjectifsQuantitatifs: qant,
            nombreObjectifsQualitatifs: qual,
            campagneFormationAssocieeId: form.campagneFormationAssocieeId ? parseInt(form.campagneFormationAssocieeId) : null,
            campagneCompetenceAssocieeId: form.campagneCompetenceAssocieeId ? parseInt(form.campagneCompetenceAssocieeId) : null,
            campagneNMoins1Id: form.campagneNMoins1Id ? parseInt(form.campagneNMoins1Id) : null,
            budgets: [],
            catalogueIds: [],
            recruitmentLimits: [],
            nombreMaxFormations: 1,
            isDefault: Boolean(form.isDefault),
            activationPurpose: form.activationPurpose,
            activationUserIds: uniqNums(activationUserIds),
            activationDateDebut: form.activationDateDebut || null,
            activationDateFin: form.activationDateFin || null,
            envoiEmails: form.envoiEmails,
            dateEnvoiEmails: form.envoiEmails === "DIFFERE" ? form.dateEnvoiEmails || null : null,
        };

        if (campagneId) {
            dispatch(updateCampagneRequest(campagneId, payload));
        } else {
            dispatch(createCampagneRequest(payload));
        }
        onClose();
    };

    const formationCampagnes = useMemo(() => (allCampagnes || []).filter(c => c.typeCampagne === "FORMATION"), [allCampagnes]);
    const skillsCampagnes = useMemo(() => (allCampagnes || []).filter(c => c.typeCampagne === "SKILLS"), [allCampagnes]);
    const performanceCampagnes = useMemo(() => (allCampagnes || []).filter(c => c.typeCampagne === "PERFORMANCE" && c.id !== campagneId), [allCampagnes, campagneId]);

    if (!open) return null;

    return (
        <div className="perf-modal-overlay" onClick={onClose}>
            <div className="perf-modal" onClick={e => e.stopPropagation()}>
                <div className="perf-modal__header">
                    <h2 className="perf-modal__title">
                        {campagneId ? `Modifier : ${campagne.nom}` : "Créer une Campagne de Performance"}
                    </h2>
                    <button className="cr-icon-btn" onClick={onClose}><X size={18} /></button>
                </div>

                <div className="cr-section-nav">
                    {[
                        { id: "id", label: "Identification" },
                        { id: "pop", label: "Population" },
                        { id: "act", label: "Activation" },
                        { id: "obj", label: "Objectifs" },
                        { id: "val", label: "Validation" },
                        { id: "rel", label: "Relances" },
                        { id: "assoc", label: "Associations" },
                        { id: "aut", label: "Autres" },
                    ].map(tab => {
                        const tabId = tab.id;
                        const label = tab.label;
                        const hasError = (tabId === "id" && (errors.nom || errors.reference)) ||
                                         (tabId === "obj" && errors.objectifs) ||
                                         (tabId === "rel" && errors.dateRelance) ||
                                         (tabId === "aut" && errors.dateEnvoiEmails);
                        return (
                            <button key={tabId} className={`cr-section-tab ${activeTab === tabId ? "active" : ""} ${hasError ? "has-error" : ""}`} onClick={() => setActiveTab(tabId)}>
                                {label}
                                {hasError && <span className="cr-tab-dot" />}
                            </button>
                        );
                    })}
                </div>

                <div className="perf-modal__body">
                    <form className="cr-form-body" id="perf-form" onSubmit={handleSubmit}>
                        {activeTab === "id" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><Info size={14} style={{verticalAlign:'middle', marginRight:6}}/>Informations Générales</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field cr-field--full">
                                        <label>Nom de la campagne *</label>
                                        <input name="nom" value={form.nom} onChange={handleChange} placeholder="Ex: EAA 2025" />
                                        {errors.nom && <span className="cr-error">{errors.nom}</span>}
                                    </div>
                                    <div className="cr-field">
                                        <label>Référence *</label>
                                        <input name="reference" value={form.reference} onChange={handleChange} placeholder="PERF-2025-001" />
                                        {errors.reference && <span className="cr-error">{errors.reference}</span>}
                                    </div>
                                    <div className="cr-field">
                                        <label>Année</label>
                                        <input type="number" name="anneeCampagne" value={form.anneeCampagne} onChange={handleChange} />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date de début</label>
                                        <input type="date" name="dateDebut" value={form.dateDebut} onChange={handleChange} className="cr-input-date" />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date de fin</label>
                                        <input type="date" name="dateFin" value={form.dateFin} onChange={handleChange} className="cr-input-date" />
                                    </div>
                                    <div className="cr-field cr-field--full">
                                        <label>Description</label>
                                        <textarea name="description" value={form.description} onChange={handleChange} rows={2} />
                                    </div>
                                    <div className="cr-field">
                                        <label>Campagne par défaut</label>
                                        <PillRadioGroup 
                                            name="isDefault"
                                            value={form.isDefault}
                                            onChange={(val) => setForm(p => ({...p, isDefault: val === "true" || val === true}))}
                                            options={[
                                                { value: true, label: "Oui" },
                                                { value: false, label: "Non" }
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "obj" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><Target size={14} style={{verticalAlign:'middle', marginRight:6}}/>Configuration des Objectifs</h3>
                                
                                <div className={`perf-summary-grid ${isExceeded ? 'has-error' : ''}`}>
                                    <div className="perf-summary-card">
                                        <span className="perf-summary-card__value">{max}</span>
                                        <span className="perf-summary-card__label">Total</span>
                                    </div>
                                    <div className="perf-summary-card">
                                        <span className="perf-summary-card__value">{qant}</span>
                                        <span className="perf-summary-card__label">Quantitatifs</span>
                                    </div>
                                    <div className="perf-summary-card">
                                        <span className="perf-summary-card__value">{qual}</span>
                                        <span className="perf-summary-card__label">Qualitatifs</span>
                                    </div>
                                    <div className={`perf-summary-card ${reste < 0 ? 'exceeded' : ''}`}>
                                        <span className="perf-summary-card__value">{reste}</span>
                                        <span className="perf-summary-card__label">Reste</span>
                                    </div>
                                </div>

                                <div className="cr-form-grid">
                                    <div className="cr-field">
                                        <label>Nombre Max d'objectifs *</label>
                                        <input type="number" name="nombreMaxObjectifs" value={form.nombreMaxObjectifs} onChange={handleChange} className="cr-input-short" />
                                        {errors.nombreMaxObjectifs && <span className="cr-error">{errors.nombreMaxObjectifs}</span>}
                                    </div>
                                    <div className="cr-field">
                                        <label>Nombre d'Objectifs Quantitatifs</label>
                                        <input type="number" name="nombreObjectifsQuantitatifs" value={form.nombreObjectifsQuantitatifs} onChange={handleChange} className="cr-input-short" />
                                    </div>
                                    <div className="cr-field">
                                        <label>Nombre d'Objectifs Qualitatifs</label>
                                        <input type="number" name="nombreObjectifsQualitatifs" value={form.nombreObjectifsQualitatifs} onChange={handleChange} className="cr-input-short" />
                                    </div>
                                    <div className="cr-field">
                                        <label>Autoriser la révision en cours d'année</label>
                                        <PillRadioGroup 
                                            name="revisionObjectifs"
                                            value={form.revisionObjectifs}
                                            onChange={(val) => setForm(p => ({...p, revisionObjectifs: val === "true" || val === true}))}
                                            options={[
                                                { value: true, label: "Oui" },
                                                { value: false, label: "Non" }
                                            ]}
                                        />
                                    </div>
                                    {errors.objectifs && <div className="cr-field cr-field--full"><span className="cr-error" style={{padding: '10px', background: '#fde8e7', borderRadius: '6px', display: 'block'}}>{errors.objectifs}</span></div>}
                                </div>
                            </div>
                        )}

                        {activeTab === "pop" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><Users size={14} style={{verticalAlign:'middle', marginRight:6}}/>Population Cible</h3>
                                
                                <div className="cr-filters-inline">
                                    <div className="cr-filter-group">
                                        <span className="cr-filter-group__label">Mode de filtrage</span>
                                        <div className="cr-filter-group__options">
                                            <PillRadioGroup 
                                                name="filterMode"
                                                value={filterMode}
                                                onChange={setFilterMode}
                                                options={[{ value: "IN", label: "IN" }, { value: "NOT_IN", label: "NOT IN" }]}
                                            />
                                        </div>
                                    </div>
                                    <div className="cr-filters-divider" />
                                    <div className="cr-filter-group">
                                        <span className="cr-filter-group__label">Niveau manager</span>
                                        <div className="cr-filter-group__options">
                                            <PillRadioGroup 
                                                name="managerLevelFilter"
                                                value={managerLevelFilter}
                                                onChange={setManagerLevelFilter}
                                                options={[{ value: "", label: "Aucun" }, { value: "n+1", label: "N+1" }, { value: "n+2", label: "N+2" }]}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="cr-form-grid" style={{ marginTop: 14 }}>
                                    <div className="cr-field">
                                        <MultiSelectDropdown 
                                            label="Entités"
                                            items={entites}
                                            value={entiteIds}
                                            onChange={setEntiteIds}
                                            getId={x => +x.id}
                                            getLabel={x => x.nom || x.name}
                                        />
                                    </div>
                                    <div className="cr-field">
                                        <MultiSelectDropdown 
                                            label="Postes"
                                            items={jobs}
                                            value={posteIds}
                                            onChange={setPosteIds}
                                            getId={x => +x.id}
                                            getLabel={x => x.nom || x.name}
                                        />
                                    </div>
                                </div>

                                <div className="cr-collab-toggle" style={{ marginTop: 16 }}>
                                    <div className="cr-collab-toggle__info">
                                        <div className="cr-strong" style={{ fontSize: 12 }}>Collaborateurs</div>
                                        <div className="cr-hint">
                                            {selectedUsers.length} sélectionné(s)
                                        </div>
                                    </div>
                                    <button type="button" className={`cr-btn ${showUsersPanel ? "cr-btn--ghost" : "cr-btn--primary"}`} onClick={() => setShowUsersPanel(!showUsersPanel)}>
                                        <Users size={14} />
                                        {showUsersPanel ? "Masquer" : "Afficher les collaborateurs"}
                                        {showUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                    </button>
                                </div>

                                {showUsersPanel && (
                                    <InlineUsersPanel 
                                        users={users}
                                        usersState={usersState}
                                        setUsersState={setUsersState}
                                        setSelectedUsers={setSelectedUsers}
                                        entiteIds={entiteIds}
                                        posteIds={posteIds}
                                    />
                                )}
                            </div>
                        )}

                        {activeTab === "act" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><Target size={14} style={{verticalAlign:'middle', marginRight:6}}/>Activation de la Campagne</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field cr-field--full">
                                        <MultiSelectDropdown 
                                            label="Pour qui (Population active)"
                                            items={filteredUsersForActivation || []}
                                            value={activationUserIds}
                                            onChange={setActivationUserIds}
                                            getId={x => +x.id}
                                            getLabel={x => `${x.firstName || x.prenom || ""} ${x.lastName || x.nom || ""}`.trim() || `#${x.id}`}
                                        />
                                    </div>
                                    <div className="cr-field">
                                        <label>Pour quoi (Objectif)</label>
                                        <PillRadioGroup 
                                            name="activationPurpose"
                                            value={form.activationPurpose}
                                            onChange={(val) => setForm(p => ({...p, activationPurpose: val}))}
                                            options={[
                                                { value: "VALIDER", label: "Valider" },
                                                { value: "TRAITER", label: "Traiter" }
                                            ]}
                                        />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date début activation</label>
                                        <input type="date" name="activationDateDebut" value={form.activationDateDebut} onChange={handleChange} className="cr-input-date" />
                                    </div>
                                    <div className="cr-field">
                                        <label>Date de fin activation</label>
                                        <input type="date" name="activationDateFin" value={form.activationDateFin} onChange={handleChange} className="cr-input-date" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "assoc" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><LinkIcon size={14} style={{verticalAlign:'middle', marginRight:6}}/>Campagnes Associées</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field">
                                        <label>Campagne de Formation liée</label>
                                        <select name="campagneFormationAssocieeId" value={form.campagneFormationAssocieeId} onChange={handleChange}>
                                            <option value="">Aucune</option>
                                            {formationCampagnes.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.anneeCampagne})</option>)}
                                        </select>
                                    </div>
                                    <div className="cr-field">
                                        <label>Campagne de Compétences liée</label>
                                        <select name="campagneCompetenceAssocieeId" value={form.campagneCompetenceAssocieeId} onChange={handleChange}>
                                            <option value="">Aucune</option>
                                            {skillsCampagnes.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.anneeCampagne})</option>)}
                                        </select>
                                    </div>
                                    <div className="cr-field cr-field--full">
                                        <label>Campagne de Performance N-1 (Historique)</label>
                                        <select name="campagneNMoins1Id" value={form.campagneNMoins1Id} onChange={handleChange}>
                                            <option value="">Aucune</option>
                                            {performanceCampagnes.map(c => <option key={c.id} value={c.id}>{c.nom} ({c.anneeCampagne})</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "val" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><ShieldCheck size={14} style={{verticalAlign:'middle', marginRight:6}}/>Niveau de Validation</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field">
                                        <label>Niveau de Validation *</label>
                                        <PillRadioGroup 
                                            name="validation"
                                            value={form.validation}
                                            onChange={(val) => setForm(p => ({...p, validation: val}))}
                                            options={[
                                                { value: "RESPONSABLE_RH", label: "RESPONSABLE RH" },
                                                { value: "MANAGER", label: "MANAGER" },
                                                { value: "BOTH", label: "LES DEUX (BOTH)" }
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "rel" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><ShieldCheck size={14} style={{verticalAlign:'middle', marginRight:6}}/>Planification des Relances</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field cr-field--full">
                                        <label>Activer les relances automatiques</label>
                                        <PillRadioGroup 
                                            name="planificationRelance"
                                            value={form.planificationRelance}
                                            onChange={(val) => setForm(p => ({...p, planificationRelance: val === "true" || val === true}))}
                                            options={[
                                                { value: true, label: "Oui" },
                                                { value: false, label: "Non" }
                                            ]}
                                        />
                                    </div>
                                    {form.planificationRelance && (
                                        <>
                                            <div className="cr-field">
                                                <label>Relance 1 *</label>
                                                <input type="date" name="dateRelance" value={form.dateRelance} onChange={handleChange} className="cr-input-date" />
                                                {errors.dateRelance && <span className="cr-error">{errors.dateRelance}</span>}
                                            </div>
                                            <div className="cr-field">
                                                <label>Relance 2</label>
                                                <input type="date" name="dateRelance2" value={form.dateRelance2} onChange={handleChange} className="cr-input-date" />
                                            </div>
                                            <div className="cr-field">
                                                <label>Relance 3</label>
                                                <input type="date" name="dateRelance3" value={form.dateRelance3} onChange={handleChange} className="cr-input-date" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        {activeTab === "aut" && (
                            <div className="cr-form-section">
                                <h3 className="cr-form-title"><Info size={14} style={{verticalAlign:'middle', marginRight:6}}/>Autres Paramètres</h3>
                                <div className="cr-form-grid">
                                    <div className="cr-field">
                                        <label>Envoi emails</label>
                                        <PillRadioGroup 
                                            name="envoiEmails"
                                            value={form.envoiEmails}
                                            onChange={(val) => setForm(p => ({...p, envoiEmails: val}))}
                                            options={[
                                                { value: "INSTANTANE", label: "Instantané" },
                                                { value: "DIFFERE", label: "Différé" },
                                                { value: "SANS_EMAIL", label: "Sans email" }
                                            ]}
                                        />
                                    </div>
                                    <div className="cr-field">
                                        <label>Statut</label>
                                        <PillRadioGroup 
                                            name="status"
                                            value={form.status}
                                            onChange={(val) => setForm(p => ({...p, status: val === "true" || val === true}))}
                                            options={[
                                                { value: true, label: "Actif" },
                                                { value: false, label: "Inactif" }
                                            ]}
                                        />
                                    </div>
                                    {form.envoiEmails === "DIFFERE" && (
                                        <div className="cr-field">
                                            <label>Date d'envoi emails *</label>
                                            <input type="date" name="dateEnvoiEmails" value={form.dateEnvoiEmails} onChange={handleChange} className="cr-input-date" />
                                            {errors.dateEnvoiEmails && <span className="cr-error">{errors.dateEnvoiEmails}</span>}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </form>
                </div>

                <div className="perf-modal__footer">
                    <button className="cr-btn cr-btn--ghost" onClick={onClose}>Annuler</button>
                    <button className={`perf-btn perf-btn--primary ${isExceeded ? 'disabled' : ''}`} disabled={isExceeded} form="perf-form" type="submit">
                        <Save size={14} />
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampagnePerformanceForm;
